import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import api from "../../utils/Api";
import axios from "axios";
import Clook from "../../components/Clock";
import { LogIn, Coffee, LogOut as LogOutIcon, AlertTriangle, X, MapPin } from "lucide-react";

const TIPO_CONFIG = {
  entrada: {
    label: "Entrada",
    labelAlt: "Entrada",
    icon: <LogIn size={28} />,
    color: "#FF7A00",
    lightBg: "var(--clr-accent-bg)",
    activeStatus: "Trabajando",
    statusColor: "#10B981",
    statusBg: "#F0FDF4",
  },
  descanso: {
    label: "Descanso",
    labelAlt: "Fin descanso",
    icon: <Coffee size={28} />,
    color: "#F59E0B",
    lightBg: "#FFFBEB",
    activeStatus: "En descanso",
    statusColor: "#B45309",
    statusBg: "#FEF3C7",
  },
  salida: {
    label: "Salida",
    labelAlt: "Salida",
    icon: <LogOutIcon size={28} />,
    color: "#EF4444",
    lightBg: "#FEF2F2",
    activeStatus: null,
    statusColor: null,
    statusBg: null,
  },
};

export default function FichajeFormEmpleado() {
  const { token, loading, user } = useContext(AuthContext);
  const [fichajes, setFichajes] = useState([]);
  const [generalError, setGeneralError] = useState("");
  const [notification, setNotification] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;
  const [modalOpen, setModalOpen] = useState(false);
  const [incidenciaComentario, setIncidenciaComentario] = useState("");
  const [incidenciaTipo, setIncidenciaTipo] = useState("incidencia");
  const [incidenciaFichajeId, setIncidenciaFichajeId] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  // ── Ubicación ────────────────────────────────────────────────────────────
  const [notaUbicacion, setNotaUbicacion]       = useState("");
  const [ubicacionLoading, setUbicacionLoading] = useState(false);
  const [ubicacionMsg, setUbicacionMsg]         = useState(null); // {type,text}

  useEffect(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    setFilterDate(`${yyyy}-${mm}-${dd}`);
  }, []);

  const parseDateTimeLocal = (fecha, hora) => {
    if (!fecha) return null;
    const dt = new Date(hora ? `${fecha}T${hora}` : `${fecha}T00:00:00`);
    return isNaN(dt.getTime()) ? null : dt;
  };

  const segundosAHMS = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return [h, m, sec].map(v => String(v).padStart(2, "0")).join(":");
  };

  const loadFichajes = () =>
    api.get("/fichajes/mios", { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setFichajes(res.data.data || []); return res.data.data || []; })
      .catch(() => { setGeneralError("Error al cargar los fichajes."); return []; });

  useEffect(() => { if (token) loadFichajes(); }, [token]);

  const getAddress = async (lat, lon) => {
    try {
      const res = await axios.get("https://nominatim.openstreetmap.org/reverse", {
        params: { lat, lon, format: "json" }, headers: { "Accept-Language": "es" }
      });
      const addr = res.data.address || {};
      return { ubicacion: addr.road || addr.neighbourhood || "Desconocida", ciudad: addr.city || addr.town || addr.village || "Desconocida" };
    } catch { return { ubicacion: "Desconocida", ciudad: "Desconocida" }; }
  };

  const ultimaEntrada = [...fichajes].filter(f => f.tipo === "entrada").reverse().find(f => {
    const salidaPosterior = fichajes.find(s => s.tipo === "salida" && parseDateTimeLocal(s.fecha, s.hora_salida) > parseDateTimeLocal(f.fecha, f.hora_entrada));
    return !salidaPosterior;
  });
  const ultimoDescanso = [...fichajes].filter(f => f.tipo === "descanso").reverse().find(f => !f.hora_fin_descanso);

  const enviarFichaje = async (tipo, geoData = {}) => {
    let endpoint = "";
    if (tipo === "entrada") endpoint = "/fichajes/startEntrada";
    else if (tipo === "descanso") {
      if (!ultimaEntrada) return setNotification("No puedes iniciar descanso sin una entrada abierta.");
      endpoint = ultimoDescanso ? "/fichajes/endDescanso" : "/fichajes/startDescanso";
    } else if (tipo === "salida") {
      if (!ultimaEntrada) return setNotification("No puedes fichar salida sin una entrada abierta.");
      if (ultimoDescanso) return setNotification("Cierra el descanso antes de fichar salida.");
      endpoint = "/fichajes/salida";
    }
    try {
      await api.post(endpoint, { usuario_id: user.id, empresa_id: user.empresa_id, ...geoData }, { headers: { Authorization: `Bearer ${token}` } });
      await loadFichajes();
      setPage(1);
      setNotification(`Fichaje de ${tipo} registrado`);
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      setGeneralError(err.response?.data?.error || "Error al registrar el fichaje.");
    }
  };

  const handleFichar = async (tipo) => {
    if (!token) return setNotification("Debes iniciar sesión primero");
    if (submittingRef.current) return;
    if (tipo === "entrada" && ultimaEntrada) { setNotification("Ya tienes una entrada activa."); return; }
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      if (!navigator.geolocation) {
        await enviarFichaje(tipo);
      } else {
        await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            async ({ coords: { latitude, longitude } }) => {
              try {
                const { ubicacion, ciudad } = await getAddress(latitude, longitude);
                await enviarFichaje(tipo, { ubicacion, ciudad, latitud: latitude, longitud: longitude });
                resolve();
              } catch (err) { reject(err); }
            },
            async () => {
              try { await enviarFichaje(tipo); resolve(); }
              catch (err) { reject(err); }
            },
            { timeout: 8000 }
          );
        });
      }
    } catch { setGeneralError("Error inesperado en el proceso de fichaje."); }
    finally { submittingRef.current = false; setIsSubmitting(false); }
  };

  const handleRegistrarUbicacion = () => {
    if (!navigator.geolocation) {
      setUbicacionMsg({ type: "error", text: "Tu dispositivo no soporta geolocalización." });
      return;
    }
    setUbicacionLoading(true);
    setUbicacionMsg(null);
    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude, longitude } }) => {
        try {
          await api.post("/ubicaciones", {
            latitud:  latitude,
            longitud: longitude,
            nota:     notaUbicacion.trim() || null,
          }, { headers: { Authorization: `Bearer ${token}` } });
          setNotaUbicacion("");
          setUbicacionMsg({ type: "ok", text: "Ubicación registrada correctamente." });
          setTimeout(() => setUbicacionMsg(null), 4000);
        } catch {
          setUbicacionMsg({ type: "error", text: "Error al registrar la ubicación." });
        } finally {
          setUbicacionLoading(false);
        }
      },
      () => {
        setUbicacionMsg({ type: "error", text: "No se pudo obtener tu ubicación. Comprueba los permisos del navegador." });
        setUbicacionLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const openModal = () => { setIncidenciaComentario(""); setIncidenciaTipo("incidencia"); setIncidenciaFichajeId(""); setValidationErrors({}); setModalOpen(true); };
  const closeModal = () => setModalOpen(false);

  const handleCrearIncidencia = async () => {
    if (!token) return setNotification("Debes iniciar sesión primero");
    if (!incidenciaComentario.trim()) return setNotification("Debes escribir un comentario");
    try {
      setValidationErrors({});
      await api.post("/incidencias", { comentario: incidenciaComentario, tipo: incidenciaTipo, fichaje_id: incidenciaFichajeId || null }, { headers: { Authorization: `Bearer ${token}` } });
      setNotification("Incidencia creada correctamente");
      setTimeout(() => setNotification(""), 4000);
      closeModal();
      loadFichajes();
    } catch (err) {
      if (err.response?.status === 422) setValidationErrors(err.response.data.errors || {});
      else setGeneralError(err.response?.data?.error || "Error al crear la incidencia.");
    }
  };

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: "var(--clr-muted)", fontSize: 14 }}>Cargando sesión…</div>;

  const fichajesFiltrados = filterDate ? fichajes.filter(f => f.fecha === filterDate) : fichajes;
  const paginatedFichajes = fichajesFiltrados.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.ceil(fichajesFiltrados.length / itemsPerPage);
  const mesActual = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  const totalSegundosUI = fichajes.filter(f => f.duracion !== null && f.fecha?.startsWith(mesActual)).reduce((a, f) => a + f.duracion, 0);

  // Estado actual del empleado
  let estadoActual = "Fuera";
  let estadoColor = "var(--clr-muted)";
  let estadoBg = "var(--clr-border-lt)";
  if (ultimoDescanso) { estadoActual = "En descanso"; estadoColor = "#B45309"; estadoBg = "#FEF3C7"; }
  else if (ultimaEntrada) { estadoActual = "Trabajando"; estadoColor = "#166534"; estadoBg = "#DCFCE7"; }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 className="tn-page-title">Control de fichajes</h1>
          <p className="tn-page-sub">Registra tu jornada laboral</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="tn-status-badge" style={{ background: estadoBg, color: estadoColor }}>{estadoActual}</span>
          <Clook />
        </div>
      </div>

      {notification && <div className="tn-alert tn-alert--info">{notification}</div>}
      {generalError && <div className="tn-alert tn-alert--danger">{generalError}</div>}

      {/* Botones de fichaje */}
      {/* <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 14, marginBottom: 28 }}> */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 14, marginBottom: 28 }}>

        {["entrada", "descanso", "salida"].map((tipo) => {
          const cfg = TIPO_CONFIG[tipo];
          const tieneEntradaActiva = !!ultimaEntrada;
          const tieneDescansoActivo = !!ultimoDescanso;
          const isActive = (tipo === "entrada" && tieneEntradaActiva) || (tipo === "descanso" && tieneDescansoActivo);
          let disabled = isSubmitting;
          if (tipo === "entrada" && tieneEntradaActiva) disabled = true;
          if (tipo === "descanso" && !tieneEntradaActiva) disabled = true;
          if (tipo === "salida" && (!tieneEntradaActiva || tieneDescansoActivo)) disabled = true;

          return (
            <div key={tipo} className={`tn-fichar-card${isActive ? " active-card" : ""}${disabled && !isActive ? " disabled-card" : ""}`}>
              <div className="tn-fichar-icon" style={{ background: cfg.lightBg, color: cfg.color }}>
                {cfg.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: "var(--clr-text)", marginBottom: 2 }}>
                  {tipo === "descanso" && tieneDescansoActivo ? cfg.labelAlt : cfg.label}
                  {isActive && (
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: cfg.statusColor || "#10B981", background: cfg.statusBg || "#F0FDF4", padding: "2px 8px", borderRadius: 100 }}>
                      Activo
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--clr-muted)", marginBottom: 14 }}>
                  {tipo === "entrada" ? "Inicia tu jornada" : tipo === "descanso" ? "Pausa activa" : "Finaliza tu jornada"}
                </div>
                <button
                  className="tn-btn tn-btn--lg"
                  style={{ background: cfg.color, color: "#fff", width: "100%", justifyContent: "center", minHeight: 48 }}
                  onClick={() => handleFichar(tipo)}
                  disabled={disabled}
                >
                  {isSubmitting ? (
                    <><span className="spinner-border spinner-border-sm" style={{ width: 16, height: 16, borderWidth: 2 }} /> Procesando…</>
                  ) : (tipo === "descanso" && tieneDescansoActivo ? "Finalizar descanso" : cfg.label)}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Registrar ubicación ─────────────────────────────────────── */}
      <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 12, padding: "18px 20px", marginBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <MapPin size={16} style={{ color: "#FF7A00" }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: "var(--clr-text)" }}>Registrar ubicación</span>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            type="text"
            value={notaUbicacion}
            onChange={e => setNotaUbicacion(e.target.value)}
            placeholder="Nota o nombre del cliente (opcional)"
            maxLength={255}
            className="tn-input"
            style={{ flex: 1, minWidth: 180, height: 40 }}
          />
          <button
            className="tn-btn tn-btn--primary"
            onClick={handleRegistrarUbicacion}
            disabled={ubicacionLoading}
            style={{ height: 40, whiteSpace: "nowrap" }}
          >
            {ubicacionLoading ? "Obteniendo GPS…" : "Registrar"}
          </button>
        </div>
        {ubicacionMsg && (
          <p style={{ margin: "8px 0 0", fontSize: 13, color: ubicacionMsg.type === "ok" ? "#16A34A" : "#EF4444" }}>
            {ubicacionMsg.text}
          </p>
        )}
      </div>

      {/* Totales + filtros */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, color: "var(--clr-muted)", fontWeight: 600 }}>Total horas (mes):</span>
          <span style={{ fontSize: 18, fontWeight: 800, color: "var(--clr-text)" }}>{segundosAHMS(totalSegundosUI)}</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input
            type="date"
            value={filterDate}
            onChange={e => { setFilterDate(e.target.value); setPage(1); }}
            className="tn-input"
            style={{ height: 38, width: "auto", minWidth: 140 }}
          />
          {filterDate && (
            <button className="tn-btn tn-btn--ghost" onClick={() => setFilterDate("")}>Limpiar</button>
          )}
          <button className="tn-btn" style={{ background: "var(--clr-warning)", color: "#fff" }} onClick={openModal}>
            <AlertTriangle size={15} /> Nueva incidencia
          </button>
        </div>
      </div>

      {/* Tabla fichajes */}
      <div className="tn-table-wrap">
        <table className="tn-table">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Fecha</th>
              <th>Hora</th>
              <th>Ciudad</th>
              <th>Duración</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {paginatedFichajes.length > 0 ? paginatedFichajes.map(f => {
              const dt = f.tipo === "entrada" ? parseDateTimeLocal(f.fecha, f.hora_entrada)
                : f.tipo === "salida" ? parseDateTimeLocal(f.fecha, f.hora_salida)
                : parseDateTimeLocal(f.fecha, f.hora_entrada_descanso);
              const cfg = TIPO_CONFIG[f.tipo] || {};
              return (
                <tr key={f.id}>
                  <td data-label="Tipo">
                    <span className="tn-badge" style={{ background: cfg.lightBg || "var(--clr-border-lt)", color: cfg.color || "var(--clr-muted)" }}>
                      {f.tipo}
                    </span>
                  </td>
                  <td data-label="Fecha">{dt ? dt.toLocaleDateString("es-ES") : ""}</td>
                  <td data-label="Hora">{dt ? dt.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : ""}</td>
                  <td data-label="Ciudad">{f.ciudad || <span style={{ color: "var(--clr-muted-lt)" }}>—</span>}</td>
                  <td data-label="Duración">{f.duracion ? <span style={{ fontWeight: 600 }}>{segundosAHMS(f.duracion)}</span> : <span style={{ color: "var(--clr-muted-lt)" }}>—</span>}</td>
                  <td data-label="Estado">
                    {f.tipo === "entrada" && f.hora_salida ? <span className="tn-badge tn-badge--green">Registrado</span>
                    : f.tipo === "descanso" && f.hora_fin_descanso ? <span className="tn-badge tn-badge--slate">Finalizado</span>
                    : f.tipo === "entrada" ? <span className="tn-badge tn-badge--orange">Activo</span>
                    : <span style={{ color: "var(--clr-muted-lt)", fontSize: 12 }}>—</span>}
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", color: "var(--clr-muted)", padding: "32px 16px", fontSize: 13.5 }}>
                  No hay fichajes para este día
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="tn-pagination">
            <button className="tn-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
            {[...Array(totalPages)].map((_, i) => (
              <button key={i} className={`tn-page-btn${page === i + 1 ? " active" : ""}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
            ))}
            <button className="tn-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          </div>
        )}
      </div>

      {/* Modal nueva incidencia */}
      {modalOpen && (
        <div className="tn-modal-backdrop" onClick={closeModal}>
          <div className="tn-modal" onClick={e => e.stopPropagation()}>
            <div className="tn-modal__header">
              <span className="tn-modal__title">Nueva incidencia</span>
              <button className="tn-close" onClick={closeModal}><X size={14} /></button>
            </div>
            <div className="tn-modal__body">
              {Object.keys(validationErrors).length > 0 && (
                <div className="tn-alert tn-alert--danger">
                  {Object.values(validationErrors).map((msgs, i) => <div key={i}>{msgs.join(", ")}</div>)}
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <label className="tn-label">Tipo de incidencia</label>
                <select className="tn-input tn-select" value={incidenciaTipo} onChange={e => setIncidenciaTipo(e.target.value)}>
                  <option value="incidencia">Incidencia</option>
                  <option value="falta">Falta</option>
                  <option value="retraso">Retraso</option>
                </select>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label className="tn-label">Asociar a fichaje (opcional)</label>
                <select className="tn-input tn-select" value={incidenciaFichajeId} onChange={e => setIncidenciaFichajeId(e.target.value)}>
                  <option value="">— No asociar —</option>
                  {fichajes.map(f => (
                    <option key={f.id} value={f.id}>
                      {f.tipo} — {f.fecha} {f.hora_entrada || f.hora_salida || f.hora_entrada_descanso}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="tn-label">Descripción</label>
                <textarea
                  className="tn-textarea"
                  rows={4}
                  placeholder="Describe la incidencia con detalle…"
                  value={incidenciaComentario}
                  onChange={e => setIncidenciaComentario(e.target.value)}
                />
              </div>
            </div>
            <div className="tn-modal__footer">
              <button className="tn-btn tn-btn--ghost" onClick={closeModal}>Cancelar</button>
              <button className="tn-btn tn-btn--primary" onClick={handleCrearIncidencia}>Enviar incidencia</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
